import { Injectable, signal } from '@angular/core';
import Keycloak, { KeycloakInitOptions, KeycloakTokenParsed } from 'keycloak-js';
import { setKeycloakCost } from '../helper/angular-helpers';
import { environment } from '../../environments/environment';

export type KeycloakEvent =
  | { type: 'onReady'; authenticated: boolean }
  | { type: 'onAuthSuccess' }
  | { type: 'onAuthError'; error: any }
  | { type: 'onAuthRefreshSuccess' }
  | { type: 'onAuthRefreshError' }
  | { type: 'onAuthLogout' }
  | { type: 'onTokenExpired' };

@Injectable({ providedIn: 'root' })
export class KeycloakService {
  private readonly keycloakInstance: Keycloak;
  readonly keyCloakEventSignal = signal<KeycloakEvent | null>(null);
  readonly token = signal<string | undefined>(undefined);

  constructor() {
    this.keycloakInstance = new Keycloak({
      url: environment.keycloak.issuer,
      realm: environment.keycloak.realm,
      clientId: environment.keycloak.clientId,
    });
    this.setupKeycloakEventListeners();
  }

  async init(): Promise<void> {
    const t0 = performance.now();

    const options: KeycloakInitOptions = {
      enableLogging: true,
      silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html',
      onLoad: 'check-sso',
      pkceMethod: 'S256',
      /*
       * NOTE: We set checkLoginIframe specifically in non-production environments, as from Keycloak 26 the session cookies adopted stricter SameSite rules.
       * The issue applies if the domain does not match the keycloak server domain (scania.com). Therefore, this may not be necessary for dev environment after domain change.
       */
      checkLoginIframe: environment.keycloak.checkLoginIframe,
    };

    try {
      console.debug('Initializing Keycloak for Mission UI');

      const authenticated = await this.keycloakInstance.init(options);

      if (!authenticated) {
        console.debug('User is not authenticated, triggering login');

        // No active session: immediately trigger login and preserve deep links.
        return await this.keycloakInstance.login({
          redirectUri: window.location.href,
        });
      }

      this.token.set(this.keycloakInstance.token);
      this.dispatchEvent();
    } catch (error) {
      console.error('Could not initialize Keycloak:', error);
      throw error;
    } finally {
      const t1 = performance.now();
      setKeycloakCost(t1 - t0);
    }
  }

  private setupKeycloakEventListeners(): void {
    this.keycloakInstance.onAuthSuccess = () => this.emitKeycloakEvent({ type: 'onAuthSuccess' });

    this.keycloakInstance.onAuthError = (error) => this.emitKeycloakEvent({ type: 'onAuthError', error });

    this.keycloakInstance.onAuthRefreshSuccess = () => this.emitKeycloakEvent({ type: 'onAuthRefreshSuccess' });

    this.keycloakInstance.onAuthRefreshError = () => this.emitKeycloakEvent({ type: 'onAuthRefreshError' });

    this.keycloakInstance.onAuthLogout = () => this.emitKeycloakEvent({ type: 'onAuthLogout' });

    this.keycloakInstance.onTokenExpired = () => this.emitKeycloakEvent({ type: 'onTokenExpired' });
  }

  private emitKeycloakEvent(event: KeycloakEvent): void {
    this.keyCloakEventSignal.set(event);
  }

  getKeyCloakInstance() {
    return this.keycloakInstance;
  }

  getParsedToken(): KeycloakTokenParsed | undefined {
    return this.keycloakInstance.tokenParsed;
  }

  logout(): void {
    this.keycloakInstance.logout();
  }

  getToken(): string | undefined {
    return this.keycloakInstance.token;
  }

  isAuthenticated(): boolean {
    return !!this.keycloakInstance.authenticated;
  }

  dispatchEvent() {
    if (this.getToken()) {
      document.dispatchEvent(
        new CustomEvent('navbar_AppLauncherUpdateToken', {
          detail: this.getToken(),
        }),
      );
    }
  }
}
